import React, { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '../api';
import { useAuth } from '../context/AuthContext';
import { css, PageHeader, Toast, Spinner, Paginacion, ESTADO_LABEL } from '../components/UI';
import { parseCFDI } from '../cfdi';

const BASE = process.env.REACT_APP_API_URL || '/api';

const TIPO_CONFIG = {
  pdf:    { label: 'PDF',    bg: 'rgba(239,68,68,0.10)', color: 'var(--danger)', border: 'rgba(239,68,68,0.28)', icon: '📄' },
  xml:    { label: 'XML',    bg: 'rgba(22,163,74,0.10)', color: 'var(--ok)', border: 'rgba(22,163,74,0.28)', icon: '📋' },
  ticket: { label: 'Imagen', bg: 'rgba(59,130,246,0.08)', color: '#1d4ed8', border: 'rgba(59,130,246,0.30)', icon: '🖼️' },
  otro:   { label: 'Otro',   bg: 'var(--hair-soft)', color: 'var(--glass)', border: 'var(--hair)', icon: '📎' },
};

const ESTADO_COLORS = { pendiente: '#b45309', aprobada: '#047857', por_pagar: '#6d28d9', rechazada: '#be123c', pagada: '#1d4ed8' };

// Pestañas. Las que traen `tipo` no son colección propia: son un tipo de solicitud
// y viven en /solicitudes (no tienen ruta propia de comprobantes).
// Todos los tipos de pago del sistema. Las que traen `tipo` son tipos de solicitud y
// viven en /solicitudes; las que no, son colecciones propias (reembolsos a cliente,
// comprobaciones y finiquitos) y su `key` ES su endpoint.
//
// Los iconos y los colores se toman de los que ya usan el menú y el Reporte Pipeline
// para el mismo tipo: un mismo gasto se ve igual en las tres pantallas.
const SECCIONES = [
  { key: 'solicitudes',             label: '📝 Gastos en General',      titulo: 'Gastos en General',      accent: '#ff541c',
    placeholder: 'Buscar por folio, proveedor o motivo...',      vacio: 'Selecciona una solicitud' },
  { key: 'debito',                  label: '🏦 Débito',                 titulo: 'Débito',                 accent: '#be123c', tipo: 'debito',
    placeholder: 'Buscar por folio, proveedor o motivo...',      vacio: 'Selecciona una solicitud de débito' },
  { key: 'contribuciones',          label: '🏛️ Contribuciones',         titulo: 'Contribuciones',         accent: '#6d28d9', tipo: 'contribuciones',
    placeholder: 'Buscar por folio o motivo...',                vacio: 'Selecciona una solicitud de contribuciones' },
  { key: 'activos_fijos',           label: '🏗️ Activos Fijos',          titulo: 'Activos Fijos',          accent: '#0f766e', tipo: 'activos_fijos',
    placeholder: 'Buscar por folio, proveedor o motivo...',      vacio: 'Selecciona una solicitud de activos fijos' },
  { key: 'mercancias_nacionales',   label: '📦 Mercancías Nacionales',  titulo: 'Mercancías Nacionales',  accent: '#b45309', tipo: 'mercancias_nacionales',
    placeholder: 'Buscar por folio, proveedor o motivo...',      vacio: 'Selecciona una solicitud de mercancías nacionales' },
  { key: 'nomina',                  label: '👥 Nómina',                 titulo: 'Nómina',                 accent: '#4338ca', tipo: 'nomina',
    placeholder: 'Buscar por folio o motivo...',                vacio: 'Selecciona una solicitud de nómina' },
  { key: 'caja_chica',              label: '🧮 Caja Chica',             titulo: 'Caja Chica',             accent: '#15803d', tipo: 'caja_chica',
    placeholder: 'Buscar por folio o motivo...',                vacio: 'Selecciona una comprobación de caja chica' },
  { key: 'reembolso_gastos',        label: '💵 Reembolso de Gastos',    titulo: 'Reembolso de Gastos',    accent: '#be185d', tipo: 'reembolso_gastos',
    placeholder: 'Buscar por folio o motivo...',                vacio: 'Selecciona una comprobación de reembolso de gastos' },
  { key: 'complemento_importacion', label: '🚢 Compl. Importación',     titulo: 'Complemento a Costo de Importación', accent: '#0f766e', tipo: 'complemento_importacion',
    placeholder: 'Buscar por folio, proveedor o motivo...',      vacio: 'Selecciona un complemento a costo de importación' },
  { key: 'reembolsos',              label: '💸 Reembolsos',             titulo: 'Reembolsos',             accent: '#0369a1',
    placeholder: 'Buscar por folio, cliente o motivo...',        vacio: 'Selecciona un reembolso' },
  { key: 'comprobaciones',          label: '🧾 Comprobaciones',         titulo: 'Comprobaciones',         accent: '#047857',
    placeholder: 'Buscar por folio, comprobador o concepto...',  vacio: 'Selecciona una comprobación' },
  { key: 'finiquitos',              label: '🤝 Finiquitos',             titulo: 'Finiquitos',             accent: '#c4a1ff',
    placeholder: 'Buscar por folio o persona...',               vacio: 'Selecciona un finiquito' },
];

const SEC = Object.fromEntries(SECCIONES.map(s => [s.key, s]));

// Colección real (ruta de API) de una pestaña.
const endpointDe = (seccion) => (SEC[seccion]?.tipo ? 'solicitudes' : seccion);

function fmtBytes(b) {
  if (!b) return '—';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
}

function fmtFecha(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function DropZone({ onFiles, uploading, progreso }) {
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef(null);
  const handle = (files) => { const arr = Array.from(files); if (arr.length) onFiles(arr); };
  return (
    <div
      onClick={() => !uploading && inputRef.current?.click()}
      onDragOver={e => { e.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={e => { e.preventDefault(); setDragging(false); handle(e.dataTransfer.files); }}
      style={{
        border: `2px dashed ${dragging ? 'var(--plasma)' : 'var(--hair)'}`,
        borderRadius: 12, padding: '28px 20px', textAlign: 'center',
        cursor: uploading ? 'not-allowed' : 'pointer',
        background: dragging ? 'rgba(255,84,28,0.04)' : 'var(--panel)',
        boxShadow: dragging ? '0 0 0 4px rgba(255,84,28,0.08)' : 'none',
        transition: 'all 0.18s', opacity: uploading ? 0.6 : 1,
      }}
    >
      <input ref={inputRef} type="file" multiple
        accept=".pdf,.xml,.jpg,.jpeg,.png,.gif,.webp,.bmp"
        style={{ display: 'none' }} onChange={e => handle(e.target.files)} />
      <div style={{ fontSize: 32, marginBottom: 8 }}>{uploading ? '⏳' : '☁️'}</div>
      <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--ink-soft)', marginBottom: 4 }}>
        {uploading
          ? (progreso ? `Subiendo ${progreso.hechos} de ${progreso.total}...` : 'Subiendo archivos...')
          : 'Arrastra archivos aquí o haz clic para seleccionar'}
      </div>
      <div style={{ fontSize: 12, color: 'var(--glass)' }}>PDF, XML, imágenes · Máx. 20 MB por archivo · sin límite de cantidad</div>
      {uploading && progreso && progreso.total > 0 && (
        <div style={{ height: 4, background: 'var(--hair)', borderRadius: 99, marginTop: 12, overflow: 'hidden' }}>
          <div style={{ height: '100%', width: `${Math.round((progreso.hechos / progreso.total) * 100)}%`, background: 'var(--plasma)', borderRadius: 99, transition: 'width 0.3s' }} />
        </div>
      )}
    </div>
  );
}

function ArchivoCard({ archivo, docId, seccion, onDeleted, canDelete, cfdi }) {
  const [deleting, setDeleting] = useState(false);
  const cfg = TIPO_CONFIG[archivo.tipo] || TIPO_CONFIG.otro;
  const token = localStorage.getItem('token');
  const basePath = `/${seccion}/${docId}/comprobantes/${archivo._id}`;

  const download = async () => {
    const r = await api.downloadFile(basePath);
    if (!r.ok) return;
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    Object.assign(document.createElement('a'), { href: url, download: archivo.nombreOriginal }).click();
    URL.revokeObjectURL(url);
  };

  const preview = () => {
    const url = `${BASE}${basePath}/preview?token=${token}`;
    window.open(url, '_blank');
  };

  const del = async () => {
    if (!window.confirm(`¿Eliminar "${archivo.nombreOriginal}"?`)) return;
    setDeleting(true);
    try {
      await api.del(basePath);
      onDeleted(archivo._id);
    } catch { setDeleting(false); }
  };

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px',
      background: 'var(--panel)', border: '1px solid var(--hair)', borderRadius: 10,
    }}
      onMouseEnter={e => e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.07)'}
      onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}
    >
      <div style={{
        width: 40, height: 40, borderRadius: 8, flexShrink: 0,
        background: cfg.bg, border: `1px solid ${cfg.border}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18,
      }}>{cfg.icon}</div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {archivo.nombreOriginal}
        </div>
        <div style={{ display: 'flex', gap: 6, marginTop: 3, alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ padding: '1px 7px', borderRadius: 4, fontSize: 10, fontWeight: 700, background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}` }}>{cfg.label}</span>
          {archivo.concepto && (
            <span style={{ padding: '1px 8px', borderRadius: 4, fontSize: 10, fontWeight: 600, background: 'rgba(22,163,74,0.10)', color: 'var(--ok)', border: '1px solid rgba(22,163,74,0.28)' }}>{archivo.concepto}</span>
          )}
          <span style={{ fontSize: 11, color: 'var(--glass)' }}>{fmtBytes(archivo.tamano)}</span>
          <span style={{ fontSize: 11, color: 'var(--glass)' }}>·</span>
          <span style={{ fontSize: 11, color: 'var(--glass)' }}>{fmtFecha(archivo.fechaSubida)}</span>
        </div>
        {/* Datos leídos del CFDI */}
        {archivo.tipo === 'xml' && cfdi === undefined && (
          <div style={{ fontSize: 10.5, color: 'var(--glass)', marginTop: 4 }}>Leyendo CFDI…</div>
        )}
        {archivo.tipo === 'xml' && cfdi && (
          <div style={{ marginTop: 5, padding: '6px 8px', background: 'var(--hair-soft)', border: '1px solid var(--hair)', borderRadius: 7, display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '2px 8px', fontSize: 11 }}>
            <span style={{ color: 'var(--glass)' }}>Proveedor</span>
            <span style={{ color: 'var(--ink)', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{cfdi.proveedor || '—'}</span>
            <span style={{ color: 'var(--glass)' }}>RFC</span>
            <span style={{ color: 'var(--ink-soft)', fontFamily: 'Geist Mono, monospace' }}>{cfdi.rfc || '—'}</span>
            <span style={{ color: 'var(--glass)' }}>Concepto</span>
            <span style={{ color: 'var(--ink-soft)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={cfdi.concepto}>{cfdi.concepto || '—'}</span>
            <span style={{ color: 'var(--glass)' }}>Monto</span>
            <span style={{ color: 'var(--ink)', fontWeight: 700, fontFamily: 'Geist Mono, monospace' }}>${(cfdi.monto || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}</span>
          </div>
        )}
      </div>

      <div style={{ display: 'flex', gap: 5, flexShrink: 0 }}>
        {['pdf', 'ticket'].includes(archivo.tipo) && (
          <button onClick={preview} title="Ver" style={{ padding: '5px 10px', borderRadius: 7, border: '1px solid var(--hair)', background: 'var(--panel)', cursor: 'pointer', fontSize: 13 }}>👁</button>
        )}
        <button onClick={download} title="Descargar" style={{ padding: '5px 10px', borderRadius: 7, border: '1px solid rgba(59,130,246,0.30)', background: 'rgba(59,130,246,0.08)', cursor: 'pointer', fontSize: 13 }}>⬇️</button>
        {canDelete && (
          <button onClick={del} disabled={deleting} title="Eliminar" style={{ padding: '5px 10px', borderRadius: 7, border: '1px solid rgba(239,68,68,0.28)', background: 'rgba(239,68,68,0.10)', cursor: deleting ? 'not-allowed' : 'pointer', fontSize: 13, color: 'var(--danger)' }}>
            {deleting ? '...' : '🗑'}
          </button>
        )}
      </div>
    </div>
  );
}

// Máximo de archivos que acepta el backend por request (upload.array('archivos', 10)).
// Subidas mayores se parten en tandas automáticas de este tamaño.
const CHUNK = 10;

function DetallePanel({ doc, seccion, onUpdate, user }) {
  const [uploading, setUploading] = useState(false);
  const [progreso, setProgreso] = useState(null);
  const [concepto, setConcepto] = useState('');
  const [conceptosCat, setConceptosCat] = useState([]);
  const [toast, setToast] = useState(null);
  const [comprobantes, setComprobantes] = useState(doc.comprobantes || []);
  const [cfdiMeta, setCfdiMeta] = useState({}); // { [fileId]: {proveedor,rfc,concepto,monto} | null(error) }
  const toast_ = (msg, tipo = 'ok') => { setToast({ msg, tipo }); setTimeout(() => setToast(null), 4000); };
  const accent = SEC[seccion].accent;
  const apiSeccion = endpointDe(seccion);

  useEffect(() => { setComprobantes(doc.comprobantes || []); }, [doc]);
  useEffect(() => {
    api.get('/conceptos-comprobante?limit=200').then(d => setConceptosCat(d.items || [])).catch(() => {});
  }, []);

  // Lee los XML (CFDI) que aún no tienen metadata: los descarga y los parsea en el navegador.
  useEffect(() => {
    let cancel = false;
    const pendientes = comprobantes.filter(a => a.tipo === 'xml' && cfdiMeta[a._id] === undefined);
    if (!pendientes.length) return;
    (async () => {
      for (const a of pendientes) {
        try {
          const r = await api.downloadFile(`/${apiSeccion}/${doc._id}/comprobantes/${a._id}`);
          if (!r.ok) throw new Error('descarga');
          const cfdi = parseCFDI(await r.text(), a.nombreOriginal);
          if (cancel) return;
          setCfdiMeta(prev => ({ ...prev, [a._id]: { proveedor: cfdi.nombreEmisor, rfc: cfdi.rfcEmisor, concepto: cfdi.concepto, monto: cfdi.total } }));
        } catch {
          if (cancel) return;
          setCfdiMeta(prev => ({ ...prev, [a._id]: null }));
        }
      }
    })();
    return () => { cancel = true; };
    // cfdiMeta se omite a propósito: cada item se procesa una vez (guard === undefined)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [comprobantes, seccion, doc._id]);

  const handleFiles = async (files) => {
    const total = files.length;
    setUploading(true);
    setProgreso({ hechos: 0, total });
    let subidos = 0;
    let ultima = null;
    try {
      // Sube en tandas secuenciales de CHUNK; el backend acumula (push), así que
      // cada respuesta trae la lista completa hasta ese punto.
      for (let i = 0; i < files.length; i += CHUNK) {
        const lote = files.slice(i, i + CHUNK);
        const fd = new FormData();
        lote.forEach(f => fd.append('archivos', f));
        if (concepto) fd.append('concepto', concepto);
        const res = await api.upload(`/${apiSeccion}/${doc._id}/comprobantes`, fd);
        ultima = res;
        subidos += (res.subidos ?? lote.length);
        setProgreso({ hechos: Math.min(i + lote.length, total), total });
        if (res.comprobantes) setComprobantes(res.comprobantes);
      }
      if (ultima?.comprobantes && onUpdate) onUpdate({ ...doc, comprobantes: ultima.comprobantes });
      toast_(`${subidos} archivo(s) subido(s) correctamente`);
    } catch (e) {
      if (ultima?.comprobantes) setComprobantes(ultima.comprobantes);
      toast_(subidos > 0 ? `Se subieron ${subidos}, pero falló una tanda: ${e.message}` : e.message, 'error');
    } finally {
      setUploading(false);
      setProgreso(null);
    }
  };

  const handleDeleted = (fileId) => setComprobantes(prev => prev.filter(f => f._id !== fileId));

  // Contador: solo sobre sus empresas; programación: sobre lo que ya ve en su lista.
  // (El backend valida de todas formas; aquí solo se oculta el botón.)
  const canDelete = user.rol === 'admin' || user.rol === 'finanzas' || user.rol === 'programacion' ||
    (user.rol === 'contador' && (user.empresas || []).includes(doc.empresa)) ||
    (String(doc.usuario?._id || doc.usuario) === String(user._id));

  const detalles = seccion === 'comprobaciones'
    ? [
        ['Comprobador',     doc.comprobador],
        ['Concepto de Gasto',doc.conceptoGasto],
        ['Compañía',         doc.empresa],
        ['Sucursal',        doc.sucursal],
        ['Centro de Costos',doc.centroCostos],
        ['Monto',           `$${(doc.monto || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })} ${doc.moneda || 'MXN'}`],
      ]
    : [
        [seccion === 'reembolsos' ? 'Cliente' : 'Proveedor', doc.proveedor],
        ['Motivo de Pago',  doc.motivoPago],
        ['Compañía',         doc.empresa],
        ['Sucursal',        doc.sucursal],
        ['Centro de Costos',doc.centroCostos],
        ['Monto',           `$${(doc.monto || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })} ${doc.moneda || 'MXN'}`],
      ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Toast msg={toast?.msg} tipo={toast?.tipo} onClose={() => setToast(null)} />

      <div style={{ background: 'var(--panel)', borderRadius: 12, border: '1px solid var(--hair)', overflow: 'hidden' }}>
        <div style={{ padding: '12px 16px', background: accent, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: 10, color: 'var(--ink-soft)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Folio</div>
            <div style={{ fontSize: 16, fontWeight: 700, color: 'white', fontFamily: 'Geist Mono, monospace' }}>{doc.folio}</div>
          </div>
          <span style={{
            padding: '4px 12px', borderRadius: 99, fontSize: 11, fontWeight: 700,
            background: `${ESTADO_COLORS[doc.estado]}25`, color: ESTADO_COLORS[doc.estado],
            border: `1.5px solid ${ESTADO_COLORS[doc.estado]}60`,
          }}>{(ESTADO_LABEL[doc.estado] || doc.estado || '').toUpperCase()}</span>
        </div>
        <div style={{ padding: '12px 16px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 16px' }}>
          {detalles.map(([l, v]) => (
            <div key={l}>
              <div style={{ fontSize: 9.5, color: 'var(--glass)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{l}</div>
              <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--ink)', marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{v || '—'}</div>
            </div>
          ))}
        </div>
      </div>

      <div style={{ background: 'var(--panel)', borderRadius: 12, border: '1px solid var(--hair)', padding: '16px' }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink-soft)', marginBottom: 12, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          Subir Comprobantes
        </div>
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--glass)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 5 }}>
            Concepto del Comprobante
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <select value={concepto} onChange={e => setConcepto(e.target.value)} style={{
              flex: 1, padding: '8px 10px', borderRadius: 8,
              border: concepto ? '1.5px solid #15803d' : '1.5px solid var(--hair)',
              background: concepto ? 'rgba(22,163,74,0.10)' : 'var(--panel)',
              color: concepto ? '#15803d' : 'var(--glass)',
              fontSize: 13, fontFamily: 'Inter, sans-serif',
              fontWeight: concepto ? 600 : 400, cursor: 'pointer', outline: 'none',
            }}>
              <option value="">— Sin concepto —</option>
              {conceptosCat.map(c => <option key={c._id} value={c.nombre}>{c.nombre}</option>)}
            </select>
            {concepto && (
              <button onClick={() => setConcepto('')} title="Limpiar" style={{
                padding: '0 12px', borderRadius: 8, border: '1px solid var(--hair)',
                background: 'var(--panel)', cursor: 'pointer', fontSize: 14, color: 'var(--glass)',
              }}>×</button>
            )}
          </div>
          {!concepto && <div style={{ fontSize: 11, color: '#f59e0b', marginTop: 4 }}>Selecciona el tipo de documento antes de subir</div>}
        </div>
        <DropZone onFiles={handleFiles} uploading={uploading} progreso={progreso} />
      </div>

      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink-soft)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Archivos subidos</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 12, color: 'var(--glass)' }}>{comprobantes.length} archivo{comprobantes.length !== 1 ? 's' : ''}</span>
            {comprobantes.length > 0 && (
              <button onClick={async () => {
                const token = localStorage.getItem('token');
                const url = `${BASE}/${apiSeccion}/${doc._id}/comprobantes/zip?token=${token}`;
                Object.assign(document.createElement('a'), { href: url, download: '' }).click();
              }} title="Descargar todos como ZIP" style={{
                padding: '3px 10px', borderRadius: 6, fontSize: 11, fontWeight: 600,
                border: '1px solid rgba(59,130,246,0.30)', background: 'rgba(59,130,246,0.08)', color: 'var(--st-por-pagar)', cursor: 'pointer',
              }}>⬇️ ZIP</button>
            )}
          </div>
        </div>
        {comprobantes.length === 0 ? (
          <div style={{ padding: '24px', textAlign: 'center', background: 'var(--panel)', borderRadius: 10, border: '1px dashed var(--hair)' }}>
            <div style={{ fontSize: 28, marginBottom: 6 }}>📂</div>
            <div style={{ fontSize: 13, color: 'var(--glass)' }}>Sin archivos adjuntos</div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {comprobantes.map(a => (
              <ArchivoCard key={a._id} archivo={a} docId={doc._id} seccion={apiSeccion}
                onDeleted={handleDeleted} canDelete={canDelete} cfdi={cfdiMeta[a._id]} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function CapturaComprobantes() {
  const { user } = useAuth();
  const [seccion, setSeccion] = useState('solicitudes');
  const [data, setData] = useState({ items: [], total: 0, pages: 1 });
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [q, setQ] = useState('');
  const [filtros, setFiltros] = useState({ empresa: '', estado: '', desde: '', hasta: '' });
  const [page, setPage] = useState(1);
  const qTimer = useRef(null);

  const load = useCallback(async (query = q, f = filtros, p = page, sec = seccion) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: p, limit: 20 });
      if (query) params.set('q', query);
      if (f.estado) params.set('estado', f.estado);
      if (f.empresa) params.set('empresa', f.empresa);
      if (f.desde) params.set('desde', f.desde);
      if (f.hasta) params.set('hasta', f.hasta);
      if (SEC[sec]?.tipo) params.set('tipo', SEC[sec].tipo);
      const res = await api.get(`/${endpointDe(sec)}?${params}`);
      setData(res);
    } catch {}
    finally { setLoading(false); }
  }, [q, filtros, page, seccion]);

  useEffect(() => { load(); }, [load]);

  const cambiarSeccion = (nueva) => {
    setSeccion(nueva);
    setSelected(null);
    setQ('');
    setFiltros({ empresa: '', estado: '', desde: '', hasta: '' });
    setPage(1);
    load('', {}, 1, nueva);
  };

  const handleQ = (val) => {
    setQ(val);
    clearTimeout(qTimer.current);
    qTimer.current = setTimeout(() => { setPage(1); load(val, filtros, 1); }, 350);
  };

  const setF = (k, v) => {
    const next = { ...filtros, [k]: v };
    setFiltros(next);
    setPage(1);
    load(q, next, 1);
  };

  const handleUpdate = (updated) => {
    setSelected(updated);
    setData(d => ({ ...d, items: d.items.map(i => i._id === updated._id ? updated : i) }));
  };

  const accent = SEC[seccion].accent;
  const ESTADOS = ['pendiente', 'aprobada', 'por_pagar', 'rechazada', 'pagada'];

  return (
    <div style={{ fontFamily: 'Inter, sans-serif' }}>
      <PageHeader
        icon="📎"
        title="Captura de Comprobantes"
        subtitle="Adjunta PDFs, XMLs y tickets a cualquier tipo de pago: gastos, débito, contribuciones, activos fijos, mercancías nacionales, nómina, caja chica, reembolso de gastos, complemento de importación, reembolsos, comprobaciones y finiquitos"
        actions={
          <button onClick={() => load()} style={{ ...css.btnGhost, border: '1px solid var(--hair)', fontSize: 12 }}>
            🔄 Actualizar
          </button>
        }
      />

      {/* Pestañas — son doce, así que van compactas y envuelven en varias filas
          sin comerse la pantalla. La activa se distingue por color, no por tamaño. */}
      <div style={{ display: 'flex', gap: 5, marginBottom: 16, flexWrap: 'wrap' }}>
        {SECCIONES.map(({ key, label, accent: a, titulo }) => (
          <button key={key} onClick={() => cambiarSeccion(key)} title={titulo} style={{
            padding: '6px 13px', borderRadius: 7, fontSize: 12, fontWeight: 600,
            fontFamily: 'Inter, sans-serif', cursor: 'pointer', transition: 'all 0.15s',
            whiteSpace: 'nowrap',
            border: `1.5px solid ${seccion === key ? a : 'var(--hair)'}`,
            background: seccion === key ? a : 'var(--hair-soft)',
            color: seccion === key ? '#0e0f11' : 'var(--glass)',
            boxShadow: seccion === key ? `0 2px 8px ${a}40` : 'none',
          }}>{label}</button>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '380px 1fr', gap: 20, alignItems: 'start' }}>

        {/* Panel izquierdo: lista */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ ...css.card, padding: 14, display: 'flex', flexDirection: 'column', gap: 8 }}>
            <input value={q} onChange={e => handleQ(e.target.value)}
              placeholder={SEC[seccion].placeholder}
              style={{ ...css.input(false) }} />
            <div style={{ display: 'flex', gap: 8 }}>
              <select value={filtros.estado} onChange={e => setF('estado', e.target.value)} style={{ ...css.select(false), flex: 1 }}>
                <option value="">Todos los estados</option>
                {ESTADOS.map(e => <option key={e} value={e}>{ESTADO_LABEL[e] || e}</option>)}
              </select>
              <button onClick={() => { setQ(''); setFiltros({ empresa: '', estado: '', desde: '', hasta: '' }); setPage(1); load('', {}, 1); }}
                style={{ ...css.btn('#6b7280', true), fontSize: 11, padding: '0 10px' }}>
                Limpiar
              </button>
            </div>
          </div>

          <div style={{ ...css.card, padding: 0, overflow: 'hidden' }}>
            <div style={{ padding: '8px 14px', background: 'var(--panel)', borderBottom: '1px solid var(--hair)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--glass)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                {SEC[seccion].titulo}
              </span>
              <span style={{ fontSize: 11, color: 'var(--glass)' }}>{data.total} total</span>
            </div>

            {loading ? (
              <div style={{ padding: 32, display: 'flex', justifyContent: 'center' }}><Spinner /></div>
            ) : data.items.length === 0 ? (
              <div style={{ padding: '32px', textAlign: 'center' }}>
                <div style={{ fontSize: 32, marginBottom: 8 }}>🔍</div>
                <div style={{ fontSize: 13, color: 'var(--glass)' }}>Sin resultados</div>
              </div>
            ) : (
              data.items.map(item => {
                const isSelected = selected?._id === item._id;
                const archCount = item.comprobantes?.length || 0;
                const mainField = seccion === 'comprobaciones' ? item.comprobador : item.proveedor;
                const subField  = seccion === 'comprobaciones' ? item.conceptoGasto : item.motivoPago;
                return (
                  <div key={item._id} onClick={() => setSelected(item)}
                    style={{
                      padding: '11px 14px', cursor: 'pointer',
                      borderBottom: '1px solid var(--hair-soft)',
                      background: isSelected ? `${accent}12` : 'var(--hair-soft)',
                      borderLeft: `3px solid ${isSelected ? accent : 'transparent'}`,
                      transition: 'all 0.12s',
                    }}
                    onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = 'rgba(255,84,28,0.04)'; }}
                    onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = 'transparent'; }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 12, fontFamily: 'Geist Mono, monospace', fontWeight: 600, color: accent }}>{item.folio}</div>
                        <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--ink)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {mainField}
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--glass)', marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {subField}
                        </div>
                      </div>
                      <div style={{ textAlign: 'right', flexShrink: 0 }}>
                        <span style={{
                          padding: '2px 8px', borderRadius: 99, fontSize: 10, fontWeight: 600,
                          background: `${ESTADO_COLORS[item.estado]}18`, color: ESTADO_COLORS[item.estado],
                        }}>{ESTADO_LABEL[item.estado] || item.estado}</span>
                        <div style={{ marginTop: 4 }}>
                          {archCount > 0 ? (
                            <span style={{ fontSize: 10, color: accent, background: `${accent}15`, padding: '1px 6px', borderRadius: 4 }}>
                              📎 {archCount}
                            </span>
                          ) : (
                            <span style={{ fontSize: 10, color: 'var(--glass)' }}>Sin archivos</span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--glass)', marginTop: 3 }}>
                      {item.empresa} · ${(item.monto || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                    </div>
                  </div>
                );
              })
            )}

            <Paginacion page={page} pages={data.pages} onPage={p => { setPage(p); load(q, filtros, p); }} />
          </div>
        </div>

        {/* Panel derecho: detalle + upload */}
        {selected ? (
          <DetallePanel key={selected._id} doc={selected} seccion={seccion}
            onUpdate={handleUpdate} user={user} />
        ) : (
          <div style={{ ...css.card, padding: '60px 32px', textAlign: 'center' }}>
            <div style={{ fontSize: 48, marginBottom: 14 }}>📎</div>
            <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--ink-soft)', marginBottom: 6 }}>
              {SEC[seccion].vacio}
            </div>
            <div style={{ fontSize: 13, color: 'var(--glass)' }}>
              Elige un registro de la lista para adjuntar sus archivos
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
